import { createFileRoute } from '@tanstack/react-router';
import { useConfirm } from '@/lib/alerts/hooks/useConfirm';
import LoadingCard from '@/lib/loading/components/LoadingCard';
import { Course } from '@/lib/models/round';
import useAxios from '@/machinery/useAxios';
import { faPencil, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@heroui/react';
import { useCallback, useState } from 'react';
import colors from 'tailwindcss/colors';
import AddEditCourseForm from '@/lib/courses/AddEditCourseForm';
import { useGet } from '@/machinery/useGet';

export const Route = createFileRoute('/courses/')({
    component: RouteComponent,
});

function RouteComponent() {
    const {
        data: courses,
        isLoading,
        isFetching,
        refetch,
    } = useGet<Course[]>({ request: { url: '/courses/user' } });
    const { ax, loading } = useAxios();
    const { showConfirm } = useConfirm();

    const [editCourse, setEditCourse] = useState<Course | undefined>(undefined);
    const [addNewCourse, setAddNewCourse] = useState(false);

    const onDelete = useCallback(
        (id: number) => {
            showConfirm({
                title: 'Are you sure you want to delete this course?',
                onConfirmed: async () => {
                    await ax({
                        method: 'delete',
                        url: `/courses/${id}`,
                    });

                    await refetch();
                },
            });
        },
        [ax, refetch, showConfirm],
    );

    const onSubmit = useCallback(
        async (data: unknown) => {
            await ax({
                method: editCourse ? 'put' : 'post',
                url: `/courses${editCourse ? '/' + editCourse.id : ''}`,
                data,
                callback: () => {
                    refetch();
                    setAddNewCourse(false);
                    setEditCourse(undefined);
                },
            });
        },
        [ax, editCourse, refetch],
    );

    return (
        <div className='mx-auto  max-w-xl p-2'>
            <div className='flex flex-row items-center justify-between  pb-2'>
                <div className='flex-initial text-lg font-semibold'>My Courses</div>
                <Button variant='light' onPress={() => setAddNewCourse(true)}>
                    Create New Course
                </Button>
            </div>
            <div className='simple-card grid w-full gap-y-4 p-3'>
                {isLoading && isFetching && <LoadingCard />}
                {courses?.map(course => {
                    return (
                        <div
                            key={course.id}
                            className='grid grid-cols-[1fr_auto_auto] items-center gap-1 p-3'>
                            <div>
                                <div>
                                    <span className='font-semibold'>{course.name}</span>
                                    <span className='pl-4 text-sm'>
                                        {course.city}, {course.state}
                                    </span>
                                </div>
                                <div className='flex flex-row gap-4 text-sm'>
                                    {course.rating && <span>Rating: {course.rating}</span>}
                                    {course.slope && <span>Rating: {course.slope}</span>}
                                </div>
                            </div>
                            <Button
                                isIconOnly
                                variant='light'
                                color='primary'
                                onPress={() => setEditCourse(course)}>
                                <FontAwesomeIcon icon={faPencil} color={colors.green[500]} />
                            </Button>
                            <Button
                                isIconOnly
                                variant='light'
                                color='danger'
                                onPress={() => onDelete(course.id)}>
                                <FontAwesomeIcon icon={faTrashCan} color={colors.red[500]} />
                            </Button>
                        </div>
                    );
                })}
            </div>
            {editCourse && (
                <AddEditCourseForm
                    onCancel={() => setEditCourse(undefined)}
                    onSubmit={onSubmit}
                    loading={loading}
                    defaultValues={{
                        city: editCourse.city,
                        name: editCourse.name,
                        state: editCourse.state,
                        rating: editCourse.rating,
                        slope: editCourse.slope,
                    }}
                />
            )}
            {addNewCourse && (
                <AddEditCourseForm
                    onCancel={() => setAddNewCourse(false)}
                    onSubmit={onSubmit}
                    loading={loading}
                    defaultValues={{
                        city: '',
                        name: '',
                        state: '',
                    }}
                />
            )}
        </div>
    );
}
